import * as XLSX from 'xlsx-js-style';
import { PortfolioReportDTO } from '@inversor-hub/types';
import {
  paymentMethodLabels,
  transactionCategoryLabels,
  transactionTypeLabels,
} from '@/features/investment-transactions/constants';
import { getInvestmentCountryLabel } from '@/features/investments/countries';

const EURO_FORMAT = '#,##0.00 [$EUR]';
const PERCENT_FORMAT = '0.0%';
const COLORS = {
  primary: '1F3A5F',
  header: '5C6B7A',
  light: 'F5F7FA',
  border: 'C8D2DC',
  white: 'FFFFFF',
  green: 'DFF3E4',
  red: 'FBE4E6',
};

const border = {
  top: { style: 'thin', color: { rgb: COLORS.border } },
  bottom: { style: 'thin', color: { rgb: COLORS.border } },
  left: { style: 'thin', color: { rgb: COLORS.border } },
  right: { style: 'thin', color: { rgb: COLORS.border } },
};

const titleStyle = {
  font: { bold: true, color: { rgb: COLORS.white }, sz: 15 },
  fill: { patternType: 'solid', fgColor: { rgb: COLORS.primary } },
  alignment: { horizontal: 'center', vertical: 'center' },
};

const headerStyle = {
  font: { bold: true, color: { rgb: COLORS.white } },
  fill: { patternType: 'solid', fgColor: { rgb: COLORS.header } },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  border,
};

const bodyStyle = {
  alignment: { vertical: 'top', wrapText: true },
  border,
};

const totalStyle = {
  font: { bold: true, color: { rgb: COLORS.primary } },
  fill: { patternType: 'solid', fgColor: { rgb: COLORS.light } },
  border,
};

function ensureCell(sheet: XLSX.WorkSheet, row: number, column: number) {
  const ref = XLSX.utils.encode_cell({ r: row, c: column });
  if (!sheet[ref]) sheet[ref] = { t: 's', v: '' };
  return sheet[ref];
}

function styleRange(
  sheet: XLSX.WorkSheet,
  startRow: number,
  endRow: number,
  startColumn: number,
  endColumn: number,
  style: Record<string, unknown>
) {
  for (let row = startRow; row <= endRow; row++) {
    for (let column = startColumn; column <= endColumn; column++) {
      ensureCell(sheet, row, column).s = style;
    }
  }
}

function formatColumn(
  sheet: XLSX.WorkSheet,
  column: number,
  startRow: number,
  endRow: number,
  format: string
) {
  for (let row = startRow; row <= endRow; row++) {
    const cell = sheet[XLSX.utils.encode_cell({ r: row, c: column })];
    if (cell) cell.z = format;
  }
}

function euros(cents: number) {
  return Number((cents / 100).toFixed(2));
}

function appendSheet(
  workbook: XLSX.WorkBook,
  name: string,
  rows: (string | number)[][],
  widths: number[],
  currencyColumns: number[],
  percentageColumns: number[] = []
) {
  const sheet = XLSX.utils.aoa_to_sheet(rows);
  const lastRow = rows.length - 1;
  const lastColumn = widths.length - 1;
  sheet['!cols'] = widths.map((wch) => ({ wch }));
  sheet['!merges'] = [XLSX.utils.decode_range(`A1:${XLSX.utils.encode_col(lastColumn)}1`)];
  sheet['!autofilter'] = {
    ref: XLSX.utils.encode_range({
      s: { r: 2, c: 0 },
      e: { r: Math.max(2, lastRow), c: lastColumn },
    }),
  };
  sheet['!freeze'] = { xSplit: 1, ySplit: 3 };
  styleRange(sheet, 0, 0, 0, lastColumn, titleStyle);
  styleRange(sheet, 2, 2, 0, lastColumn, headerStyle);
  if (lastRow >= 3) styleRange(sheet, 3, lastRow, 0, lastColumn, bodyStyle);
  for (const column of currencyColumns) {
    formatColumn(sheet, column, 3, lastRow, EURO_FORMAT);
  }
  for (const column of percentageColumns) {
    formatColumn(sheet, column, 3, lastRow, PERCENT_FORMAT);
  }
  XLSX.utils.book_append_sheet(workbook, sheet, name);
  return sheet;
}

export function exportPortfolioReport(report: PortfolioReportDTO) {
  const isSingleInvestment = report.investments.length === 1;
  const reportName = isSingleInvestment ? report.investments[0].name : 'cartera';
  const countryLabel = getInvestmentCountryLabel(report.country);
  const reportTitle = isSingleInvestment
    ? `Informe de inmueble · ${reportName} · ${countryLabel} · ${report.year}`
    : `Informe de cartera · ${countryLabel} · ${report.year}`;
  const workbook = XLSX.utils.book_new();
  const summaryRows: (string | number)[][] = [
    [reportTitle, '', '', '', '', '', '', ''],
    [],
    [
      'Inmueble',
      'Código de identificación',
      'Dirección',
      'Ingresos',
      'Gastos',
      'Neto',
      'Neto mensual medio',
      'Movimientos',
    ],
    ...report.investments.map((investment) => [
      investment.name,
      investment.identificationCode ?? '',
      `${investment.address}, ${investment.city}`,
      euros(investment.totals.income),
      euros(investment.totals.expenses),
      euros(investment.totals.net),
      euros(investment.totals.monthlyAverageNet),
      investment.totals.transactionCount,
    ]),
    [
      isSingleInvestment ? 'TOTAL INMUEBLE' : 'TOTAL CARTERA',
      '',
      '',
      euros(report.totals.income),
      euros(report.totals.expenses),
      euros(report.totals.net),
      euros(report.totals.monthlyAverageNet),
      report.totals.transactionCount,
    ],
  ];
  const summarySheet = appendSheet(
    workbook,
    isSingleInvestment ? 'Resumen inmueble' : 'Resumen cartera',
    summaryRows,
    [28, 24, 42, 15, 15, 15, 20, 14],
    [3, 4, 5, 6]
  );
  styleRange(summarySheet, summaryRows.length - 1, summaryRows.length - 1, 0, 7, totalStyle);

  const monthNames = Array.from({ length: 12 }, (_, index) =>
    new Intl.DateTimeFormat('es-ES', { month: 'short' }).format(
      new Date(Date.UTC(report.year, index, 1))
    )
  );
  const monthlyRows: (string | number)[][] = [
    [`Evolución mensual · ${countryLabel} · ${report.year}`, ...Array(13).fill('')],
    [],
    ['Inmueble', 'Código de identificación', ...monthNames, 'Total'],
    ...report.investments.map((investment) => [
      investment.name,
      investment.identificationCode ?? '',
      ...investment.months.map((month) => euros(month.net)),
      euros(investment.totals.net),
    ]),
    [
      isSingleInvestment ? 'TOTAL INMUEBLE' : 'TOTAL CARTERA',
      '',
      ...report.months.map((month) => euros(month.net)),
      euros(report.totals.net),
    ],
  ];
  const monthlySheet = appendSheet(
    workbook,
    'Evolución mensual',
    monthlyRows,
    [28, 24, ...Array(13).fill(13)],
    Array.from({ length: 13 }, (_, index) => index + 2)
  );
  styleRange(monthlySheet, monthlyRows.length - 1, monthlyRows.length - 1, 0, 14, totalStyle);

  const categoryRows: (string | number)[][] = [
    [`Gastos por categoría · ${countryLabel} · ${report.year}`, '', '', '', ''],
    [],
    [
      'Inmueble',
      'Código de identificación',
      'Categoría',
      'Importe',
      '% gastos inmueble',
      'Movimientos',
    ],
    ...report.investments.flatMap((investment) =>
      investment.expenseCategories.map((category) => [
        investment.name,
        investment.identificationCode ?? '',
        transactionCategoryLabels[category.category],
        euros(category.amount),
        category.percentage,
        category.transactionCount,
      ])
    ),
  ];
  appendSheet(workbook, 'Gastos por categoría', categoryRows, [28, 24, 34, 15, 18, 14], [3], [4]);

  const transactionRows: (string | number)[][] = [
    [`Movimientos · ${countryLabel} · ${report.year}`, '', '', '', '', '', '', '', ''],
    [],
    [
      'Inmueble',
      'Código de identificación',
      'Fecha',
      'Tipo',
      'Concepto',
      'Categoría',
      'Importe',
      'Método de pago',
      'Pendiente de revisar',
    ],
    ...report.investments.flatMap((investment) =>
      investment.transactions.flatMap((transaction) =>
        transaction.lines.map((line) => [
          investment.name,
          investment.identificationCode ?? '',
          new Date(transaction.date).toLocaleDateString('es-ES'),
          transactionTypeLabels[transaction.type],
          transaction.concept ?? 'Sin concepto',
          transactionCategoryLabels[line.category],
          euros(line.amount),
          transaction.paymentMethod ? paymentMethodLabels[transaction.paymentMethod] : '',
          line.category === 'REVIEW' ? 'Sí' : 'No',
        ])
      )
    ),
  ];
  appendSheet(workbook, 'Movimientos', transactionRows, [28, 24, 13, 12, 32, 34, 15, 18, 18], [6]);

  const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array', cellStyles: true });
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const slug = reportName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  link.download = `informe-${slug || 'cartera'}-${report.country.toLowerCase()}-${report.year}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
